'use client';

import { ApplicationUserMenu } from '@unifyapps/carbon/components/ApplicationUserMenu';
import type { ShowDrawer } from '@unifyapps/carbon/components/Drawer';
import { getNavItemWithAdaptedUrl } from '@unifyapps/carbon/components/NavigationContainer/utils';
import type { UserMenuProps } from '@unifyapps/carbon/components/UserMenu';
import { NotificationsHelper } from '@unifyapps/carbon/helpers/NotificationsHelper';
import { InterfaceModes } from '@unifyapps/carbon/no-code/stores/InterfaceStore';
import type {
  InterfaceEntity,
  NavigationResolution,
  PageNavItem,
  UrlNavItem,
} from '@unifyapps/defs/types/interface';
import type { BlockThemeEnum } from '@unifyapps/defs/types/styleObject';
import clsx from 'clsx';
import type { CSSProperties, RefObject } from 'react';
import { useMemo } from 'react';
import type { SecondaryCustomizers } from '../../hooks/useAppNavigationModel';

type Args = {
  interfaceRecord?: InterfaceEntity;
  permissibleUserMenuItems: (UrlNavItem | PageNavItem)[] | undefined;
  hideDrawer?: () => void;
  onProfileClick: () => void;
  theme: BlockThemeEnum | undefined;
  isTopPositioned: boolean;
  unopenedNotificationsCount?: number;
  showDrawer: ShowDrawer;
  resolution: NavigationResolution;
  sideNavRef: RefObject<HTMLDivElement | null>;
  navigationContainerRef: RefObject<HTMLDivElement | null>;
  borderProps: { className: string; style: CSSProperties | undefined };
};

export function useSecondaryCustomizers({
  interfaceRecord,
  permissibleUserMenuItems,
  hideDrawer,
  onProfileClick,
  theme,
  isTopPositioned,
  unopenedNotificationsCount,
  showDrawer,
  resolution,
  sideNavRef,
  navigationContainerRef,
  borderProps,
}: Args): SecondaryCustomizers {
  return useMemo<SecondaryCustomizers>(
    () => ({
      buildUserMenu: (item, { user: ctxUser }) => ({
        type: 'USER_MENU',
        id: item.id,
        user: { name: ctxUser.name, email: ctxUser.email },
        onClick: hideDrawer,
        // Theme classes (INTERFACE_THEME / UA_THEME_VARS) intentionally not
        // passed here — UserNavigationItem renders the menu via Popover with
        // useAnchorAsContainer (disablePortal=true), so it inherits theme vars
        // from the matrix root layout instead of needing its own wrapper.
        // eslint-disable-next-line react/no-unstable-nested-components -- inside memo
        MenuList: ({ onClose, activePathnames }) => (
          <ApplicationUserMenu
            activePathnames={activePathnames}
            email={ctxUser.email}
            menuItems={
              interfaceRecord && permissibleUserMenuItems
                ? (permissibleUserMenuItems.map((menuItem) => {
                    switch (menuItem.type) {
                      case 'URL':
                      case 'PAGE':
                        return getNavItemWithAdaptedUrl({
                          navItem: menuItem,
                          interfaceRecord,
                          mode: InterfaceModes.RUNNER,
                        });
                      default:
                        return menuItem;
                    }
                  }) as UrlNavItem[] | undefined)
                : undefined
            }
            name={ctxUser.name}
            onClose={onClose}
            onProfileClick={onProfileClick}
            scheme={theme as UserMenuProps['scheme']}
          />
        ),
        popupProps: {
          placement: !isTopPositioned ? 'right-end' : 'bottom-start',
          maxWidth: 20,
          minWidth: 20,
          className: clsx('shadow-xl', {
            '-mt-[10px] mr-lg': isTopPositioned,
            'mb-md': !isTopPositioned,
          }),
        },
      }),
      buildAction: (item) => {
        if (item.action !== 'showNotifications') return item;
        return {
          ...item,
          actionProps: { unopenedNotificationsCount: unopenedNotificationsCount ?? 0 },
          icon: 'SvgNotification',
          onClick: ({ onOpen, onClose }) => {
            // Compute drawer styles at click time — relies on refs being attached
            // and reads current DOM measurements, avoiding stale-state pitfalls
            // of the previous useEffect + setState pattern.
            const drawerProps = NotificationsHelper.getDrawerStyles(
              interfaceRecord,
              resolution,
              // Since in runner mode, we do not have Interface builder Header
              0,
              sideNavRef,
              navigationContainerRef,
              borderProps,
              isTopPositioned,
            );

            showDrawer({
              anchor: isTopPositioned ? 'right' : 'left',
              template: 'notifications',
              callbacks: { onShow: onOpen, onHide: onClose },
              // TODO: remove this once get the complete Design for top position
              theme: isTopPositioned ? 'light' : theme,
              slotProps: NotificationsHelper.getNotificationDrawerSlotProps(
                drawerProps,
                isTopPositioned,
              ),
              componentProps: {
                notificationTags: interfaceRecord?.properties.notifications?.tags,
              },
            });
          },
        };
      },
    }),
    [
      hideDrawer,
      onProfileClick,
      theme,
      isTopPositioned,
      permissibleUserMenuItems,
      interfaceRecord,
      unopenedNotificationsCount,
      showDrawer,
      resolution,
      sideNavRef,
      navigationContainerRef,
      borderProps,
    ],
  );
}
